import React from 'react';
import DataRow, { DataRowHeader } from './ui/DataRow';
import Field from './ui/Field';
import Section from './ui/Section';

/*
 * Who has asked to enter a competition.
 *
 * Two coloured card headers over a competition picker made of full-width
 * buttons, and a bordered table under a third. Now the same shape as the rest
 * of admin: a white surface, sections with hairlines, one row per record.
 *
 * The competition picker is a dropdown, not a list: a club can have dozens,
 * and as rows they filled the screen so you scrolled past all of them to
 * reach the registrations, which are the point of the page.
 */

const STATUS_CLASS = {
  approved: 'w3-green',
  rejected: 'w3-red',
  pending: 'w3-orange',
};

function AdminRegistrationsTab({ comps, handleReg, loadRegs, qrData, regs, selectedCompRegs, setQrData, showQR }) {
  const selectedComp = comps.find(c => c.id === selectedCompRegs);

  const columns = [
    {
      key: 'player', label: 'Player', grow: true, primary: true, minWidth: 160,
      render: r => (r.User ? r.User.name : 'Unknown'),
    },
    {
      key: 'team', label: 'Team', grow: true, minWidth: 140,
      render: r => (r.teamName
        ? <span style={{ color: 'var(--gs-text-muted)' }}>{r.teamName}</span>
        : <span style={{ color: 'var(--gs-text-faint)' }}>—</span>),
    },
    {
      key: 'status', label: 'Status', width: 110,
      render: r => (
        <span className={`w3-tag w3-round ${STATUS_CLASS[r.status] || 'w3-grey'}`}
          style={{ fontSize: 'var(--gs-text-xs)' }}>
          {r.status}
        </span>
      ),
    },
    {
      key: 'date', label: 'Registered', width: 120,
      render: r => (
        <span style={{ color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
          {new Date(r.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions', label: '', width: 190, align: 'right',
      // Only a pending registration has anything to decide.
      render: r => (r.status === 'pending' ? (
        <span style={{ display: 'flex', gap: 'var(--gs-space-2)', justifyContent: 'flex-end' }}>
          <button className="w3-button w3-round"
            style={{ minHeight: 'var(--gs-control-sm)', background: 'var(--gs-accent-fill, var(--club-30))', color: '#fff', fontWeight: 600 }}
            onClick={() => handleReg(selectedCompRegs, r.id, 'approved')}>
            Approve
          </button>
          <button className="w3-button w3-round w3-hover-red"
            style={{ minHeight: 'var(--gs-control-sm)', border: '1px solid var(--gs-border)', background: 'var(--gs-surface)' }}
            onClick={() => handleReg(selectedCompRegs, r.id, 'rejected')}>
            Reject
          </button>
        </span>
      ) : null),
    },
  ];

  return (
    <div className="gs-panel">
      {/* A club can have dozens of competitions. As a list of rows the picker
          filled the screen and you scrolled past all of them to reach the
          registrations, which are the point of the page. Past four options a
          dropdown is the right control. */}
      <Section label="Registrations for">
        <div style={{ display: 'flex', gap: 'var(--gs-space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="Competition" htmlFor="reg-comp" style={{ flex: '1 1 320px', maxWidth: 460 }}>
            <select id="reg-comp" className="w3-select"
              value={selectedCompRegs || ''}
              onChange={e => e.target.value && loadRegs(Number(e.target.value))}>
              <option value="">Choose a competition…</option>
              {comps.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          {selectedCompRegs && (
            <button className="w3-button w3-round w3-theme-l4"
              style={{ minHeight: 'var(--gs-control-md)', whiteSpace: 'nowrap' }}
              onClick={() => showQR(selectedCompRegs)}>
              <i className="fa fa-qrcode w3-margin-right"></i>Entry QR code
            </button>
          )}
        </div>
      </Section>

      {selectedCompRegs && (
        <Section label={`${selectedComp ? selectedComp.name : 'Registrations'} — ${regs.length} registration${regs.length === 1 ? '' : 's'}`}>
          {regs.length === 0 ? (
            <p style={{ color: 'var(--gs-text-faint)', margin: 0, padding: 'var(--gs-space-4) 0' }}>
              Nobody has entered this competition yet.
            </p>
          ) : (
            <>
              <DataRowHeader columns={columns} />
              {regs.map(r => <DataRow key={r.id} columns={columns} item={r} />)}
            </>
          )}
        </Section>
      )}

      {qrData && (
        <div className="w3-modal" style={{ display: 'block' }}>
          <div className="w3-modal-content w3-round" style={{ maxWidth: '400px' }}>
            <div className="w3-container w3-theme-d4 w3-round-top" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700 }}>Registration QR code</span>
              <button className="w3-button w3-round" onClick={() => setQrData(null)} aria-label="Close">&times;</button>
            </div>
            <div className="w3-container w3-padding w3-center">
              <h4>{qrData.competitionName}</h4>
              <img src={qrData.qrCode} alt="" style={{ width: '280px', height: '280px' }} />
              <p style={{ color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)', wordBreak: 'break-all' }}>{qrData.url}</p>
              <button className="w3-button w3-round w3-margin-bottom"
                style={{ minHeight: 'var(--gs-control-md)', background: 'var(--gs-accent-fill, var(--club-30))', color: '#fff', fontWeight: 600 }}
                onClick={() => { navigator.clipboard.writeText(qrData.url); }}>
                Copy link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminRegistrationsTab;
